import {
  Edit3,
  PauseCircle,
  PlayCircle,
  Power,
  Star,
} from 'lucide-react';

import { formatMoney } from '../../../../utils/decimalValue';
import RestaurantTable, {
  RestaurantTableColumn,
} from '../../shared/RestaurantTable';
import { MenuItem } from '../types';

interface Props {
  items: MenuItem[];
  onEdit: (item: MenuItem) => void;
  onToggleAvailable: (item: MenuItem) => void;
  onToggleActive: (item: MenuItem) => void;
}

const MenuTable = ({
  items,
  onEdit,
  onToggleAvailable,
  onToggleActive,
}: Props) => {
  const columns: RestaurantTableColumn<MenuItem>[] = [
    {
      key: 'code',
      header: 'Código',
      className: 'whitespace-nowrap font-semibold text-primary',
      cell: (item) => item.code,
    },
    {
      key: 'name',
      header: 'Producto',
      cell: (item) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-black dark:text-white">
            {item.displayName || item.productName}
          </span>
          {item.featured && (
            <span
              title="Destacado"
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-semibold text-warning"
            >
              <Star className="h-3 w-3 fill-current" />
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Categoría',
      className: 'text-sm text-bodydark2',
      cell: (item) => item.categoryName,
    },
    {
      key: 'price',
      header: 'Precio',
      className: 'whitespace-nowrap font-semibold text-black dark:text-white',
      cell: (item) => formatMoney(item.productPrice),
    },
    {
      key: 'prep',
      header: 'Preparación',
      className: 'whitespace-nowrap text-sm text-bodydark2',
      cell: (item) =>
        item.preparationTimeMinutes !== null
          ? `${item.preparationTimeMinutes} min`
          : '—',
    },
    {
      key: 'station',
      header: 'Estación',
      className: 'text-sm text-bodydark2',
      cell: (item) => item.preparationStationName || '—',
    },
    {
      key: 'availability',
      header: 'Disponibilidad',
      cell: (item) => (
        <span
          className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
            item.effectiveAvailable
              ? 'border-success/20 bg-success/10 text-success'
              : 'border-danger/20 bg-danger/10 text-danger'
          }`}
        >
          {item.effectiveAvailable ? 'Disponible' : 'No disponible'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (item) => (
        <span
          className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
            item.active
              ? 'border-success/20 bg-success/10 text-success'
              : 'border-danger/20 bg-danger/10 text-danger'
          }`}
        >
          {item.active ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      headClassName: 'text-right',
      cell: (item) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onEdit(item)}
            className="inline-flex items-center gap-2 rounded-xl border border-stroke px-3 py-2 text-sm font-medium transition hover:border-primary hover:bg-primary/5 hover:text-primary dark:border-strokedark"
          >
            <Edit3 className="h-4 w-4" /> Editar
          </button>

          <button
            type="button"
            disabled={!item.active}
            title={item.available ? 'Pausar' : 'Habilitar'}
            onClick={() => onToggleAvailable(item)}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
              item.available
                ? 'border-stroke text-warning hover:border-warning hover:bg-warning/5 dark:border-strokedark'
                : 'border-success/30 bg-success/5 text-success hover:bg-success hover:text-white'
            }`}
          >
            {item.available ? (
              <PauseCircle className="h-4 w-4" />
            ) : (
              <PlayCircle className="h-4 w-4" />
            )}
            {item.available ? 'Pausar' : 'Habilitar'}
          </button>

          <button
            type="button"
            title={item.active ? 'Desactivar' : 'Reactivar'}
            onClick={() => onToggleActive(item)}
            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition ${
              item.active
                ? 'border-stroke text-danger hover:border-danger hover:bg-danger/5 dark:border-strokedark'
                : 'border-primary/30 bg-primary/5 text-primary hover:bg-primary hover:text-white'
            }`}
          >
            <Power className="h-4 w-4" />
            {item.active ? 'Desactivar' : 'Reactivar'}
          </button>
        </div>
      ),
    },
  ];

  return (
    <RestaurantTable
      columns={columns}
      rows={items}
      rowKey={(item) => item.id}
      rowClassName={(item) => (item.active ? '' : 'opacity-65')}
      minWidth="min-w-[1150px]"
    />
  );
};

export default MenuTable;
